import { SegmentedControl as MantineSegmentedControl } from '@mantine/core'
import styled from 'styled-components'

/**
 * Horizontal segmented control — a group of mutually-exclusive segments where
 * exactly one is selected at a time. Wraps Mantine 7 `SegmentedControl` with the
 * `styled(...)` theme-token convention used by `switch.tsx`.
 */
export const SegmentedControl = styled(MantineSegmentedControl)`
  ${({ theme }) =>
    `
    &.mantine-SegmentedControl-root {
      min-height: 40px;
      padding: ${theme.spacing.xxs};
      gap: ${theme.spacing.xxs};
      background-color: ${theme.scale.neutral100};
      border-radius: ${theme.radii.interactive};
      --separator-color: transparent;
      /* Mantine's root is inline-flex unless the consumer passes fullWidth; this
         design is always full-width. */
      display: flex;
      width: 100%;
    }

    /* Drop Mantine's divider lines so the track reads as one rounded surface. */
    .mantine-SegmentedControl-control::before {
      background-color: transparent;
    }

    /* Mantine's animated indicator is the white selected pill. */
    .mantine-SegmentedControl-indicator {
      background-color: ${theme.scale.white};
      box-shadow: ${theme.boxShadows.small};
      border-radius: ${theme.radii.interactive};
    }

    .mantine-SegmentedControl-label {
      height: 36px;
      display: flex;
      align-items: center;
      justify-content: center;
      padding-top: ${theme.spacing.xs};
      padding-bottom: ${theme.spacing.xs};
      border-radius: ${theme.radii.interactive};
      font-size: ${theme.typographyTokens.bodyMd.fontSize};
      /* No lemonade token for Inter Medium 14/20. */
      line-height: 20px;
      font-weight: 500;
    }

    .mantine-SegmentedControl-control {
      background-color: transparent;
      /* min-width: 0 overrides the flex-item default so long labels truncate
         (Mantine sets text-overflow) instead of widening their segment. */
      flex: 1 0 0;
      min-width: 0;
    }

    /* Legacy primitive: theme.semantic.contentTertiary still resolves to the old
       #6D6A73 — the same exception stat-card.tsx documents. The
       :not([data-disabled]) guard stops this higher-specificity rule clobbering
       Mantine's zero-specificity disabled colour. */
    .mantine-SegmentedControl-label:not([data-active]):not([data-disabled]) {
      color: ${theme.colors.contentTertiary};
    }

    .mantine-SegmentedControl-label:not([data-active]):not([data-disabled]):hover {
      color: ${theme.scale.neutral700};
    }

    .mantine-SegmentedControl-label[data-active] {
      color: ${theme.scale.neutral900};
    }

    /* Mantine's focusable element is a visually-hidden radio, so style its
       adjacent label. The border sits on the label's own box (follows the radius,
       never clipped) and border-box keeps the 36px height stable so focus causes
       no layout shift. theme.colors.primary400 is the legacy pink, hence the
       scale primitive. */
    .mantine-SegmentedControl-input:focus-visible + .mantine-SegmentedControl-label {
      box-sizing: border-box;
      border: 1px solid ${theme.scale.primary400};
      position: relative;
      z-index: 2;
    }

    .mantine-SegmentedControl-input:focus-visible + .mantine-SegmentedControl-label:not([data-active]) {
      color: ${theme.scale.neutral700};
    }
  `}
`
